if (!customElements.get('the-fold-bounded-carousel')) {
  customElements.define('the-fold-bounded-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewport = this.querySelector('[data-bounded-viewport]');
      this.track = this.querySelector('[data-bounded-track]');
      this.slides = [...this.querySelectorAll('[data-bounded-slide]')];
      this.dots = [...this.querySelectorAll('[data-bounded-dot]')];
      this.previous = this.querySelector('[data-bounded-previous]');
      this.next = this.querySelector('[data-bounded-next]');
      if (!this.viewport || !this.track || !this.slides.length) return;
      this.index = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previous?.addEventListener('click', () => { this.userPositioned = true; this.goTo(this.index - 1); }, { signal });
      this.next?.addEventListener('click', () => { this.userPositioned = true; this.goTo(this.index + 1); }, { signal });
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.boundedDot)), { signal }));
      this.addEventListener('keydown', (event) => {
        if (event.target.matches('input, select, textarea, video')) return;
        const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (direction || event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.userPositioned = true;
          this.goTo(direction ? this.index + direction : event.key === 'Home' ? 0 : this.points.length - 1);
        }
      }, { signal });
      this.viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button, video, model-viewer')) return;
        this.userPositioned = true;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0, offset: this.offset, time: performance.now(), moving: false };
      }, { signal });
      this.viewport.addEventListener('pointermove', (event) => {
        const drag = this.drag;
        if (drag?.id !== event.pointerId) return;
        const delta = event.clientX - drag.x;
        if (!drag.moving) {
          if (Math.abs(event.clientY - drag.y) > Math.abs(delta)) { this.drag = null; return; }
          if (Math.abs(delta) <= 5) return;
          drag.moving = true;
          this.viewport.setPointerCapture(event.pointerId);
        }
        drag.delta = delta;
        const limit = this.points.at(-1);
        let offset = drag.offset - delta;
        if (offset < 0) offset = -Math.pow(-offset, .85);
        else if (offset > limit) offset = limit + Math.pow(offset - limit, .85);
        this.render(false, offset);
      }, { signal });
      for (const type of ['pointerup', 'pointercancel']) {
        this.viewport.addEventListener(type, (event) => {
          const drag = this.drag;
          if (drag?.id !== event.pointerId) return;
          this.drag = null;
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          this.suppressClick = drag.moving;
          let target = this.index;
          if (type !== 'pointercancel' && drag.moving) {
            const offset = drag.offset - drag.delta;
            if (performance.now() - drag.time > 300) {
              target = this.points.reduce((best, point, index) => Math.abs(point - offset) < Math.abs(this.points[best] - offset) ? index : best, 0);
            } else if (drag.delta < 0) {
              target = this.points.findIndex((point) => point >= offset);
              if (target < 0) target = this.points.length - 1;
            } else {
              target = this.points.findLastIndex((point) => point <= offset);
            }
          }
          this.goTo(target);
        }, { signal });
      }
      this.viewport.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClick = false;
      }, { signal, capture: true });
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener('focusin', (event) => {
        if (this.dataset.kind !== 'patterns') return;
        const slide = event.target.closest('[data-bounded-slide]');
        // Pointer focus must not move a link between pointerdown and click.
        if (!slide?.matches(':focus-visible')) return;
        // Reveal keyboard focus with our transform, without native focus scrolling.
        this.viewport.scrollLeft = 0;
        const bounds = this.viewport.getBoundingClientRect();
        const position = slide.getBoundingClientRect();
        if (position.left >= bounds.left && position.right <= bounds.right) return;
        const index = this.slides.indexOf(slide);
        if (index >= 0) {
          this.userPositioned = true;
          this.goTo(Math.min(index, this.points.length - 1));
        }
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-bounded-slide]'));
        if (index >= 0) {
          this.userPositioned = true;
          this.goTo(index);
        }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      this.measure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    measure() {
      if (this.dataset.kind !== 'patterns') {
        this.slides.forEach((slide) => { slide.style.width = `${this.viewport.clientWidth}px`; });
      }
      const first = this.slides[0].offsetLeft;
      const last = this.slides.at(-1);
      const trailingMargin = this.dataset.kind === 'patterns' ? Number.parseFloat(getComputedStyle(last).marginRight) || 0 : 0;
      const end = last.offsetLeft + last.getBoundingClientRect().width + trailingMargin;
      const max = Math.max(0, end - first - this.viewport.clientWidth);
      this.points = this.slides.map((slide) => slide.offsetLeft - first).filter((offset) => offset < max - .5);
      this.points.push(max);
      this.index = Math.max(0, Math.min(this.index, this.points.length - 1));
      let renderOffset;
      if (this.dataset.kind === 'patterns' && !this.userPositioned) {
        const current = this.slides.find((slide) => slide.getAttribute('aria-current') === 'page');
        const currentOffset = current ? current.offsetLeft - first : 0;
        const currentWidth = current?.getBoundingClientRect().width || 0;
        const visibleOffset = currentOffset - (this.offset ?? this.points[this.index]);
        const currentIsVisible = visibleOffset >= 0 && visibleOffset + currentWidth <= this.viewport.clientWidth;
        this.centerCurrentPattern ||= Boolean(current && !currentIsVisible);
        if (this.centerCurrentPattern) {
          const centeredOffset = Math.max(0, Math.min(max, currentOffset + currentWidth / 2 - this.viewport.clientWidth / 2));
          this.index = this.points.reduce((closest, point, index) =>
            Math.abs(point - centeredOffset) < Math.abs(this.points[closest] - centeredOffset) ? index : closest, 0);
          renderOffset = centeredOffset;
        }
      }
      this.render(false, renderOffset);
    }

    goTo(index) {
      this.index = Math.max(0, Math.min(index, this.points.length - 1));
      this.render(!this.reducedMotion.matches);
    }

    render(animate, offset = this.points[this.index]) {
      this.offset = offset;
      this.track.style.transition = animate ? 'transform 300ms ease' : 'none';
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.points.length - 1;
      this.dots.forEach((dot) => {
        if (Number(dot.dataset.boundedDot) === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.dataset.kind === 'gallery') {
        const thumbnail = this.dots[this.index];
        if (thumbnail) {
          const parent = thumbnail.parentElement;
          const left = thumbnail.offsetLeft;
          if (left < parent.scrollLeft) parent.scrollLeft = left;
          else if (left + thumbnail.offsetWidth > parent.scrollLeft + parent.clientWidth) parent.scrollLeft = left + thumbnail.offsetWidth - parent.clientWidth;
        }
      }
      if (this.dataset.kind !== 'patterns') {
        this.slides.forEach((slide, index) => {
          slide.inert = index !== this.index;
          slide.setAttribute('aria-hidden', String(index !== this.index));
          if (index !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
      }
    }
  });
}
